import { type TypedDocumentNode, useLazyQuery, useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';

export type DuplicateBusinessEntityRole = 'CUSTOMER' | 'VENDOR';

export type DuplicateBusinessEntityMatch = 'NAME' | 'BLUE_BOOK_ID' | 'DUNS_NUMBER' | 'ADDRESS_AND_SIMILAR_NAME';

export type DuplicateBusinessEntityMember = {
  entity: { id: number; name: string; roles: string[] | null; bluebookId: string | null };
  orderCount: number;
  billCount: number;
  contactCount: number;
  lastOrderDate: string | null;
  city: string | null;
  state: string | null;
};

export type DuplicateBusinessEntityGroup = {
  key: string;
  matchedOn: DuplicateBusinessEntityMatch[];
  members: DuplicateBusinessEntityMember[];
};

export type BusinessEntityMergePreview = {
  survivorId: number;
  duplicateIds: number[];
  referenceCounts: { reference: string; count: number }[];
};

export type BusinessEntityMergeRecord = {
  id: number;
  survivorId: number;
  duplicateIds: number[];
  undoneAt: string | null;
  searchSyncPending: boolean;
};

type FindData = { findDuplicateBusinessEntities: DuplicateBusinessEntityGroup[] };
type FindVariables = { input: { role: DuplicateBusinessEntityRole } };
type PreviewData = { previewBusinessEntityMerge: BusinessEntityMergePreview };
type MergeVariables = { survivorId: number; duplicateIds: number[] };
type MergeData = { mergeBusinessEntities: BusinessEntityMergeRecord };
type UndoData = { undoBusinessEntityMerge: BusinessEntityMergeRecord };
type UndoVariables = { id: number };

const FIND_DUPLICATES: TypedDocumentNode<FindData, FindVariables> = gql`
  query BusinessEntityDuplicatesQuery($input: FindDuplicateBusinessEntitiesInput!) {
    findDuplicateBusinessEntities(input: $input, take: 50) {
      key
      matchedOn
      members {
        entity {
          id
          name
          roles
          bluebookId
        }
        orderCount
        billCount
        contactCount
        lastOrderDate
        city
        state
      }
    }
  }
`;

const PREVIEW_MERGE: TypedDocumentNode<PreviewData, MergeVariables> = gql`
  query BusinessEntityMergePreviewQuery($survivorId: Int!, $duplicateIds: [Int!]!) {
    previewBusinessEntityMerge(survivorId: $survivorId, duplicateIds: $duplicateIds) {
      survivorId
      duplicateIds
      referenceCounts {
        reference
        count
      }
    }
  }
`;

const MERGE_BUSINESS_ENTITIES: TypedDocumentNode<MergeData, MergeVariables> = gql`
  mutation MergeBusinessEntities($survivorId: Int!, $duplicateIds: [Int!]!) {
    mergeBusinessEntities(survivorId: $survivorId, duplicateIds: $duplicateIds) {
      id
      survivorId
      duplicateIds
      undoneAt
      searchSyncPending
    }
  }
`;

const UNDO_BUSINESS_ENTITY_MERGE: TypedDocumentNode<UndoData, UndoVariables> = gql`
  mutation UndoBusinessEntityMerge($id: Int!) {
    undoBusinessEntityMerge(id: $id) {
      id
      survivorId
      duplicateIds
      undoneAt
      searchSyncPending
    }
  }
`;

/** All duplicate-cleanup data doors live here so the modal stays presentation-only. */
export function useDuplicateBusinessEntities() {
  const [lastMerge, setLastMerge] = useState<BusinessEntityMergeRecord | null>(null);
  const [runFind, findState] = useLazyQuery(FIND_DUPLICATES, { fetchPolicy: 'network-only' });
  const [runPreview, previewState] = useLazyQuery(PREVIEW_MERGE, { fetchPolicy: 'network-only' });
  const [runMerge, mergeState] = useMutation(MERGE_BUSINESS_ENTITIES);
  const [runUndo, undoState] = useMutation(UNDO_BUSINESS_ENTITY_MERGE);

  const find = useCallback(
    (role: DuplicateBusinessEntityRole) =>
      runFind({ variables: { input: { role } } }).then((result) => {
        if (result.error) throw result.error;
      }),
    [runFind]
  );
  const preview = useCallback(
    (survivorId: number, duplicateIds: number[]) =>
      runPreview({ variables: { survivorId, duplicateIds } }).then((result) => {
        if (result.error) throw result.error;
      }),
    [runPreview]
  );
  const merge = useCallback(
    async (survivorId: number, duplicateIds: number[]) => {
      const result = await runMerge({ variables: { survivorId, duplicateIds } });
      const record = result.data?.mergeBusinessEntities;
      if (!record) throw new Error('The merge did not return a record.');
      setLastMerge(record);
      return record;
    },
    [runMerge]
  );
  const undo = useCallback(
    async (id: number) => {
      const result = await runUndo({ variables: { id } });
      const record = result.data?.undoBusinessEntityMerge;
      if (!record) throw new Error('The undo did not return a record.');
      setLastMerge(record);
      return record;
    },
    [runUndo]
  );

  return {
    groups: findState.data?.findDuplicateBusinessEntities ?? [],
    previewResult: previewState.data?.previewBusinessEntityMerge ?? null,
    lastMerge,
    find,
    preview,
    merge,
    undo,
    finding: findState.loading,
    previewing: previewState.loading,
    merging: mergeState.loading,
    undoing: undoState.loading,
  };
}
